import React, { Suspense } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { NavLink } from 'react-router-dom';
import {
  HomeIcon,
  EnvelopeOpenIcon,
  ChatBubbleLeftRightIcon,
  UserPlusIcon,
  VideoCameraIcon,
} from '@heroicons/react/24/solid';
import ErrorBoundaryEmpty from '../ErrorBoundaryEmpty';
import Spinner from '../Spinner';
import { IconAddressBook } from '../Icons';

const CLASS_WEBTEST_NAVBAR_MESSAGES = 'webtest-navBarMessages';

const Collapsed = ({
  onActionCallback,
  unreadMessages,
  chatPath,
  unreadInvitationRequestCount,
  inboxInMenuCollapsed,
}) => {
  const { t } = useTranslation('components');

  const CLASSES_ACTIVE = 'active text-blue-600';
  const CLASSES_ITEM_BOX = 'w-full h-10 mb-2 lg:mb-4 flex justify-center items-center';
  const CLASSES_ITEM = 'w-8 h-8 p-1 rounded text-slate-500 lg:hover:text-blue-500';
  const CLASSES_ICON = 'w-7 h-7 icon';

  return (
    <>
      <NavLink
        to="/"
        exact
        className={`${CLASSES_ITEM_BOX} ${CLASSES_ITEM}`}
        activeClassName={CLASSES_ACTIVE}
        onClick={onActionCallback}
        title={t('PhysicianNavBar.menu_items.home')}
      >
        <HomeIcon className={CLASSES_ICON} />
      </NavLink>

      <NavLink
        to="/inbox/requests"
        className={`relative ${CLASSES_ITEM_BOX} ${CLASSES_ITEM} ${CLASS_WEBTEST_NAVBAR_MESSAGES}`}
        activeClassName={CLASSES_ACTIVE}
        isActive={(_, location) => location.pathname.startsWith('/inbox/requests')}
        onClick={() => {
          if (onActionCallback) onActionCallback();
        }}
        title={t('PhysicianNavBar.menu_items.inbox')}
      >
        <EnvelopeOpenIcon className={CLASSES_ICON} />
        <Suspense
          fallback={
            <div className="flex w-full items-center justify-center">
              <Spinner size="3xl" />
            </div>
          }
        >
          <ErrorBoundaryEmpty>
            <div className="absolute ml-4 bottom-[0.05rem]">{inboxInMenuCollapsed}</div>
          </ErrorBoundaryEmpty>
        </Suspense>
      </NavLink>

      <NavLink
        to={!chatPath ? '/chat' : chatPath}
        className={`relative ${CLASSES_ITEM_BOX} ${CLASSES_ITEM} ${CLASS_WEBTEST_NAVBAR_MESSAGES}`}
        activeClassName={CLASSES_ACTIVE}
        onClick={() => {
          if (onActionCallback) onActionCallback();
        }}
        title={t('PhysicianNavBar.menu_items.messages', { allowHtml: true })}
      >
        <ChatBubbleLeftRightIcon className={CLASSES_ICON} />
        {unreadMessages > 0 && (
          <div className="absolute" style={{ marginLeft: '1rem', bottom: '0.05rem' }}>
            <div className="h-3 w-3 rounded-full bg-red-600" />
          </div>
        )}
      </NavLink>

      <NavLink
        to="/address-book"
        className={`relative ${CLASSES_ITEM_BOX} ${CLASSES_ITEM} ${CLASS_WEBTEST_NAVBAR_MESSAGES}`}
        activeClassName={CLASSES_ACTIVE}
        onClick={() => {
          if (onActionCallback) onActionCallback();
        }}
        title={t('PhysicianNavBar.menu_items.contacts', { allowHtml: true })}
      >
        <IconAddressBook size="10" className="icon" />
      </NavLink>

      <NavLink
        to="/invitation-requests"
        className={`relative ${CLASSES_ITEM_BOX} ${CLASSES_ITEM} ${CLASS_WEBTEST_NAVBAR_MESSAGES}`}
        activeClassName={CLASSES_ACTIVE}
        onClick={() => {
          if (onActionCallback) onActionCallback();
        }}
        title={t('PhysicianNavBar.menu_items.invitation_requests')}
      >
        <UserPlusIcon className={CLASSES_ICON} />
        {unreadInvitationRequestCount > 0 && (
          <div className="absolute" style={{ marginLeft: '1rem', bottom: '0.05rem' }}>
            <div className="h-3 w-3 rounded-full bg-red-600" />
          </div>
        )}
      </NavLink>

      <NavLink
        to="/sessions"
        className={`relative ${CLASSES_ITEM_BOX} ${CLASSES_ITEM} ${CLASS_WEBTEST_NAVBAR_MESSAGES}`}
        activeClassName={CLASSES_ACTIVE}
        onClick={() => {
          if (onActionCallback) onActionCallback();
        }}
        title={t('PhysicianNavBar.menu_items.video_lists')}
      >
        <VideoCameraIcon className={CLASSES_ICON} />
      </NavLink>
    </>
  );
};

Collapsed.propTypes = {
  onActionCallback: PropTypes.func,
  unreadMessages: PropTypes.number,
  chatPath: PropTypes.string,
  unreadInvitationRequestCount: PropTypes.number,
  inboxInMenuCollapsed: PropTypes.node,
};

export default Collapsed;
